<i18n src="@/locales/models/inventory_movement.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/service_provider/warehouse/movements/show_warehouse.yml"
  lang="yaml"
></i18n>
<template>
  <div class="mt-4">
    <h1 class="page-title">
      <i class="fa-solid fa-arrow-right-arrow-left"></i>
      <span class="space-icon"></span>
      {{ $t("labels.inventory_movements_title") }}
    </h1>

    <div class="flex flex-row justify-end gap-x-2">
      <button
        type="button"
        @click.prevent="goToNewMovement(0)"
        class="btn-white"
      >
        <i class="fa-solid fa-plus"></i>
        <span class="space-icon"></span>
        {{ $t("labels.new_inbound_button") }}
      </button>
      <button
        type="button"
        @click.prevent="goToNewMovement(1)"
        class="btn-white"
      >
        <i class="fa-solid fa-plus"></i>
        <span class="space-icon"></span>
        {{ $t("labels.new_outbound_button") }}
      </button>
    </div>

    <div class="mt-2 flex flex-col">
      <ag-grid-vue
        style="height: 620px"
        class="ag-theme-alpine"
        @grid-ready="onGridReady"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="inventory_movements"
      >
      </ag-grid-vue>
    </div>

    <table-paginator
      v-model="current_page"
      v-if="total_rows > rows_per_page"
      :total_rows="total_rows"
      :rows_per_page="rows_per_page"
    >
    </table-paginator>
  </div>
</template>

<script>
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridVue } from "ag-grid-vue3";
import Requester from "@/views/service_provider/requesters/warehouse/movements/show_warehouse.js";
import ManageRequester from "@/views/service_provider/requesters/warehouse/movements/manage_inventory_movements.js";
import { dateFilter } from "@/components/views/mixins/ag_grid_filters.js";

import TableActions from "@/components/views/layout/ag_grid/TableActions.vue";
import TablePaginator from "@/components/views/layout/TablePaginator.vue";

export default {
  name: "PHProductionBasicContainerExportShowProcessContainers",
  components: { AgGridVue, TablePaginator },
  data() {
    return {
      inventory_movements: [],
      current_page: 1,
      total_rows: 0,
      rows_per_page: 12,
      defaultColDef: {
        sortable: true,
        filter: true,
        floatingFilter: true,
      },
      columnDefs: [
        {
          headerName: this.$t("model.inventory_movement.movement_type"),
          field: "movement_type",
          width: 50,
          cellRendererSelector: (params) => {
            const GoToLink = {
              template: `<span>
              <i class="fa-solid ${
                params.value == "inbound"
                  ? "fa-right text-green-600"
                  : "fa-left text-red-600"
              }"></i>
              ${params.value == "inbound" ? "Entrada" : "Salida"}
              </span>`,
            };
            return {
              component: GoToLink,
            };
          },
        },
        {
          headerName: this.$t("model.inventory_movement.registered_at"),
          field: "registered_at",
          width: 100,
          valueFormatter: (params) =>
            this.dateFormatter(params.data.registered_at),
          filter: "agDateColumnFilter",
          filterParams: dateFilter,
        },
        {
          headerName: this.$t("model.inventory_movement.inventory_item"),
          field: "inventory_item.name",
          filter: true,
        },
        {
          headerName: this.$t("model.inventory_movement.quantity"),
          field: "quantity",
          filter: "agNumberColumnFilter",
        },
        {
          headerName: this.$t("model.inventory_movement.comments"),
          field: "comments",
          filter: true,
        },
        {
          headerName: this.$t("model.inventory_movement.document_number"),
          field: "document_number",
          filter: true,
        },
        {
          headerName: this.$t("model.inventory_movement.supplier"),
          field: "supplier.name",
          filter: true,
        },
        {
          headerName: "",
          width: 200,
          filter: false,
          cellRendererSelector: (params) => {
            return {
              component: TableActions,
              params: {
                edit_label: "",
                editEvent: this.goToEditMovement,
                show_destroy: true,
                destroy_label: "",
                destroy_title: this.$t("labels.destroy_movement_button_title", {
                  item: params.data.name,
                }),
                destroy_message: this.$t(
                  "labels.destroy_movement_button_message",
                  {
                    item: params.data.name,
                  }
                ),
                destroy_label_confirm: this.$t(
                  "labels.destroy_movement_button_label_confirm"
                ),
                destroyEvent: this.destroy,
              },
            };
          },
        },
      ],
    };
  },
  props: {
    service_provider_id: { required: true },
    warehouse_id: { required: true },
  },

  created() {
    this.fetchInventoryMovements();
  },

  methods: {
    fetchInventoryMovements: async function () {
      let response = await Requester.fetchInventoryMovements(
        this.service_provider_id,
        this.warehouse_id,
        this.current_page,
        this.rows_per_page
      );
      this.inventory_movements = response.data.records;
      this.total_rows = response.data.total_rows;
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.sizeToFit();
    },

    sizeToFit() {
      this.gridApi.sizeColumnsToFit({ defaultMinWidth: 150 });
    },

    destroy: async function (movement) {
      try {
        const index = this.inventory_movements.indexOf(movement);
        let response = await ManageRequester.destroy(
          this.service_provider_id,
          this.warehouse_id,
          movement.id
        );
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.inventory_movements.splice(index, 1);
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      }
    },

    goToNewMovement: function (movement_type) {
      this.$router.push({
        name: this.routeName("new_movement"),
        params: {
          service_provider_id: this.service_provider_id,
          warehouse_id: this.warehouse_id,
          movement_type: movement_type,
        },
      });
    },

    goToEditMovement: function (item) {
      this.$router.push({
        name: this.routeName("edit_movement"),
        params: {
          service_provider_id: this.service_provider_id,
          warehouse_id: this.warehouse_id,
          id: item.id,
        },
      });
    },
  },
  watch: {
    current_page: async function () {
      this.load_data = false;
      await this.fetchInventoryMovements();
      this.load_data = true;
    },
  },
};
</script>
